import React, { useState, useRef, useEffect } from "react";
import { chinaToNairobiDisplay } from "../../timeUtilis";
import "./ExecutiveSummary.css";

// Helper function to get adjusted duration
const getAdjustedDuration = (order, adjustDurationsEnabled) => {
  if (!adjustDurationsEnabled) return order.billingDuration || 0;
  
  // Use adjusted duration if available, otherwise cap at 180
  if (order.billingDuration !== undefined) {
    return Math.min(order.billingDuration, 180);
  }
  return 0;
};

// Format duration for display
const formatDuration = (minutes, order = null, adjustEnabled = false) => {
  const duration = adjustEnabled && order ? getAdjustedDuration(order, adjustEnabled) : minutes;
  
  if (!duration || duration === 0) return "0 hrs";

  const hours = duration / 60;

  if (hours < 1) {
    const display = `${Math.round(duration)} min`;
    // Add indicator if duration was adjusted
    if (adjustEnabled && order && order.billingDuration && order.billingDuration > 180) {
      return `${display} ⚡`;
    }
    return display;
  } else if (hours === 1) {
    const display = "1 hr";
    if (adjustEnabled && order && order.billingDuration && order.billingDuration > 180) {
      return `${display} ⚡`;
    }
    return display;
  } else if (hours < 24) {
    const display = `${hours.toFixed(1)} hrs`;
    if (adjustEnabled && order && order.billingDuration && order.billingDuration > 180) {
      return `${display} ⚡`;
    }
    return display;
  } else {
    const days = Math.floor(hours / 24);
    const remainingHours = (hours % 24).toFixed(1);

    let display;
    if (remainingHours === "0.0") {
      display = `${days} day${days > 1 ? "s" : ""}`;
    } else {
      display = `${days} day${days > 1 ? "s" : ""} ${remainingHours} hrs`;
    }
    
    if (adjustEnabled && order && order.billingDuration && order.billingDuration > 180) {
      return `${display} ⚡`;
    }
    return display;
  }
};

// Format duration for table display
const formatDurationForTable = (minutes, order = null, adjustEnabled = false) => {
  const duration = adjustEnabled && order ? getAdjustedDuration(order, adjustEnabled) : minutes;
  
  if (!duration || duration === 0) return "0 min";

  const hours = duration / 60;

  if (hours < 1) {
    const display = `${Math.round(duration)} min`;
    if (adjustEnabled && order && order.billingDuration && order.billingDuration > 180) {
      return `${display} ⚡`;
    }
    return display;
  } else {
    const fullHours = Math.floor(hours);
    const remainingMinutes = Math.round((hours - fullHours) * 60);

    let display;
    if (remainingMinutes === 0) {
      display = `${fullHours} hr${fullHours > 1 ? "s" : ""}`;
    } else {
      display = `${fullHours} hr${fullHours > 1 ? "s" : ""} ${remainingMinutes} min`;
    }
    
    if (adjustEnabled && order && order.billingDuration && order.billingDuration > 180) {
      return `${display} ⚡`;
    }
    return display;
  }
};

// PAGE: Shows important numbers at the top
const ExecutiveSummary = ({ orders, dateRange, adjustDurationsEnabled = false }) => {
  // STATE: Track selected KPI for detailed view
  const [selectedKPI, setSelectedKPI] = useState(null);
  // STATE: Pagination for tables
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);

  // REFS: For animations and outside clicks
  const overlayRef = useRef(null);
  const contentRef = useRef(null);
  const tableContainerRef = useRef(null);
  const kpiCardsRef = useRef([]);

  // HELPER: Determine order status based on data
  const getOrderStatus = (order) => {
    if (
      (order.pGhtime && order.pGhtime.trim() !== "") ||
      (order.pJiesuan && order.pJiesuan > 0)
    ) {
      return "completed";
    } else {
      return "pending";
    }
  };

  // FUNCTION: Count and calculate all the numbers we need
  const calculateMetrics = () => {
    if (!orders || orders.length === 0) {
      return {
        totalOrders: 0,
        completedOrders: 0,
        unreturnedOrders: 0,
        uniqueUsers: 0,
        totalRevenue: 0,
        avgRevenue: 0,
        avgDuration: 0,
        avgDurationHours: 0,
        allOrders: [],
        completedOrdersList: [],
        unreturnedOrdersList: [],
        revenueOrders: [],
        durationOrders: [],
        users: [],
        adjustedOrdersCount: 0,
        totalAdjustedMinutes: 0,
      };
    }

    // Calculate adjustment statistics
    let adjustedOrdersCount = 0;
    let totalAdjustedMinutes = 0;
    
    if (adjustDurationsEnabled) {
      orders.forEach(order => {
        if (order.billingDuration && order.billingDuration > 180) {
          adjustedOrdersCount++;
          totalAdjustedMinutes += (order.billingDuration - 180);
        }
      });
    }

    const totalOrders = orders.length;
    const completedOrdersList = orders.filter(
      (o) =>
        (o.pGhtime && o.pGhtime.trim() !== "") || (o.pJiesuan && o.pJiesuan > 0)
    );
    const unreturnedOrdersList = orders.filter(
      (o) =>
        !(
          (o.pGhtime && o.pGhtime.trim() !== "") ||
          (o.pJiesuan && o.pJiesuan > 0)
        )
    );

    const completedOrders = completedOrdersList.length;
    const unreturnedOrders = unreturnedOrdersList.length;
    const users = [...new Set(orders.map((o) => o.fUsername).filter(Boolean))];
    const uniqueUsers = users.length;

    const revenueOrders = completedOrdersList;
    const totalRevenue = revenueOrders.reduce(
      (sum, o) => sum + (o.pJiesuan || 0),
      0
    );

    const avgRevenue =
      completedOrders > 0 ? (totalRevenue / completedOrders).toFixed(2) : 0;

    const durationOrders = orders;
    const totalDurationMinutes = durationOrders.reduce(
      (sum, o) => sum + getAdjustedDuration(o, adjustDurationsEnabled),
      0
    );

    const avgDurationMinutes =
      totalOrders > 0 ? (totalDurationMinutes / totalOrders).toFixed(1) : 0;

    const avgDurationHours =
      totalOrders > 0
        ? (totalDurationMinutes / totalOrders / 60).toFixed(2)
        : 0;

    return {
      totalOrders,
      completedOrders,
      unreturnedOrders,
      uniqueUsers,
      totalRevenue,
      avgRevenue: parseFloat(avgRevenue),
      avgDuration: parseFloat(avgDurationMinutes),
      avgDurationHours: parseFloat(avgDurationHours),
      allOrders: orders,
      completedOrdersList,
      unreturnedOrdersList,
      revenueOrders,
      durationOrders,
      users,
      adjustedOrdersCount,
      totalAdjustedMinutes,
    };
  };

  const metrics = calculateMetrics();

  // Format date range for display
  const formatDateRange = () => {
    if (!dateRange || !dateRange.start || !dateRange.end) return "All Time";
    return `${dateRange.start.toLocaleDateString()} - ${dateRange.end.toLocaleDateString()}`;
  };

  // KPI Definitions with theme colors
  const kpiCards = [
    {
      id: "total-orders",
      title: "Total Orders",
      value: metrics.totalOrders,
      icon: "fas fa-shopping-cart",
      color: "#5e52e0",
      gradient: "linear-gradient(135deg, #5e52e0 0%, #5448fa 100%)",
      data: metrics.allOrders,
      trend: "+12%",
    },
    {
      id: "completed",
      title: "Completed Orders",
      value: metrics.completedOrders,
      icon: "fas fa-check-circle",
      color: "#4b6d22",
      gradient: "linear-gradient(135deg, #4b6d22 0%, #5a8c2e 100%)",
      data: metrics.completedOrdersList,
      trend: "+8%",
    },
    {
      id: "unreturned",
      title: "Unreturned Orders",
      value: metrics.unreturnedOrders,
      icon: "fas fa-clock",
      color: "#ef2f6e",
      gradient: "linear-gradient(135deg, #ef2f6e 0%, #ff4d8d 100%)",
      data: metrics.unreturnedOrdersList,
      trend: "-5%",
    },
    {
      id: "revenue",
      title: "Total Revenue",
      value: `Ksh ${metrics.totalRevenue.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      icon: "fas fa-money-bill-wave",
      color: "#581f66",
      gradient: "linear-gradient(135deg, #581f66 0%, #6b2780 100%)",
      data: metrics.revenueOrders,
      trend: "+15%",
    },
    {
      id: "avg-revenue",
      title: "Avg Revenue",
      value: `Ksh ${metrics.avgRevenue.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      icon: "fas fa-chart-line",
      color: "#b52a6b",
      gradient: "linear-gradient(135deg, #b52a6b 0%, #d4347a 100%)",
      data: metrics.revenueOrders,
      trend: "+3%",
    },
    {
      id: "avg-duration",
      title: adjustDurationsEnabled ? "Avg Duration (Adjusted)" : "Avg Duration",
      value: formatDuration(metrics.avgDuration),
      icon: "fas fa-stopwatch",
      color: "#311a64",
      gradient: "linear-gradient(135deg, #311a64 0%, #3d1f7a 100%)",
      data: metrics.durationOrders,
      trend: "-2%",
      showAdjustmentInfo: adjustDurationsEnabled && metrics.adjustedOrdersCount > 0,
      adjustmentInfo: adjustDurationsEnabled ? `⚡ ${metrics.adjustedOrdersCount} orders adjusted` : null,
    },
  ];

  // Pagination logic
  const getCurrentPageData = (data) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  const totalPages = (data) => Math.ceil(data.length / itemsPerPage);

  // Smart pagination - show limited page numbers
  const getVisiblePages = (data) => {
    const totalPageCount = totalPages(data);
    const maxVisiblePages = 7;
    const current = currentPage;
    const pages = [];

    if (totalPageCount <= maxVisiblePages) {
      return Array.from({ length: totalPageCount }, (_, i) => i + 1);
    }

    pages.push(1);

    let startPage = Math.max(2, current - 2);
    let endPage = Math.min(totalPageCount - 1, current + 2);

    if (current <= 3) {
      endPage = Math.min(totalPageCount - 1, maxVisiblePages - 1);
    }

    if (current >= totalPageCount - 2) {
      startPage = Math.max(2, totalPageCount - (maxVisiblePages - 2));
    }

    if (startPage > 2) {
      pages.push("...");
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPageCount - 1) {
      pages.push("...");
    }

    if (totalPageCount > 1) {
      pages.push(totalPageCount);
    }

    return pages;
  };

  // Better scroll to top function
  const scrollToTop = () => {
    const scrollOptions = { top: 0, behavior: "smooth" };

    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTo(scrollOptions);
    }

    if (contentRef.current) {
      contentRef.current.scrollTo(scrollOptions);
    }

    window.scrollTo(scrollOptions);

    setTimeout(() => {
      window.scrollTo(scrollOptions);
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTo(scrollOptions);
      }
    }, 100);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    scrollToTop();
  };

  // FUNCTION: Handle KPI card click
  const handleKpiClick = (kpiId) => {
    setSelectedKPI(kpiId);
    setCurrentPage(1);
    setTimeout(scrollToTop, 150);
  };

  // FUNCTION: Handle back to KPIs grid
  const handleBackToGrid = () => {
    setSelectedKPI(null);
    setCurrentPage(1);
  };

  // FUNCTION: Handle KPI change from dropdown
  const handleKpiChange = (e) => {
    setSelectedKPI(e.target.value || null);
    setCurrentPage(1);
    setTimeout(scrollToTop, 150);
  };

  // Get data for selected KPI
  const getDataForKpi = (kpiId) => {
    const kpi = kpiCards.find((k) => k.id === kpiId);
    return kpi ? kpi.data : [];
  };

  // Get title for selected KPI
  const getTitleForKpi = (kpiId) => {
    const kpi = kpiCards.find((k) => k.id === kpiId);
    return kpi ? kpi.title : "";
  };

  // EFFECT: Handle outside clicks to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        selectedKPI &&
        overlayRef.current &&
        !contentRef.current.contains(event.target)
      ) {
        handleBackToGrid();
      }
    };

    if (selectedKPI) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedKPI]);

  // EFFECT: Add animation to KPI cards
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = "1";
            entry.target.style.transform = "translateY(0)";
          }
        });
      },
      { threshold: 0.1 }
    );

    kpiCardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  // Add card to ref array
  const addToRefs = (el, index) => {
    if (el && !kpiCardsRef.current.includes(el)) {
      kpiCardsRef.current[index] = el;
    }
  };

  // Render KPI Grid View
  if (!selectedKPI) {
    return (
      <div className="executive-summary">
        <div className="summary-header">
          <div className="header-content">
            <h2 className="exsection-title">
              <i className="fas fa-chart-line pulse"></i> Executive Summary
              {/* {adjustDurationsEnabled && (
                <span className="adjustment-indicator" title="Duration adjustment is active">
                  ⚡ Adjusted
                </span>
              )} */}
            </h2>
            <p className="date-range">
              <i className="fas fa-calendar-alt"></i> {formatDateRange()}
              {adjustDurationsEnabled && metrics.adjustedOrdersCount > 0 && (
                <span className="adjustment-stats">
                  <i className="fas fa-clock" style={{marginLeft: '10px'}}></i>
                  {metrics.adjustedOrdersCount} orders capped at 3hrs
                </span>
              )}
            </p>
          </div>
          <div className="header-stats">
            <div className="stat-bubble">
              <span className="stat-value">{metrics.totalOrders}</span>
              <span className="stat-label">Total Orders</span>
            </div>
            <div className="stat-bubble">
              <span className="stat-value">
                Ksh {metrics.totalRevenue.toLocaleString()}
              </span>
              <span className="stat-label">Revenue</span>
            </div>
          </div>
        </div>

        {/* KPI Cards Grid */}
        <div className="kpi-grid">
          {kpiCards.map((card, idx) => (
            <div
              key={card.id}
              ref={(el) => addToRefs(el, idx)}
              className="kpi-card"
              onClick={() => handleKpiClick(card.id)}
              title={card.showAdjustmentInfo ? card.adjustmentInfo : ""}
            >
              <div
                className="card-glow"
                style={{ background: card.gradient }}
              ></div>
              <div className="card-content">
                <div className="card-header">
                  <div
                    className="card-icon"
                    style={{ background: card.gradient }}
                  >
                    <i className={card.icon}></i>
                  </div>
                </div>
                <div className="card-body">
                  <h3 className="ecard-value">
                    {card.value}
                    {card.showAdjustmentInfo && (
                      <span className="card-adjustment-indicator" title={card.adjustmentInfo}>
                        ⚡
                      </span>
                    )}
                  </h3>
                  <p className="ecard-title">{card.title}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Selected KPI Detailed View
  const selectedData = getDataForKpi(selectedKPI);
  const selectedTitle = getTitleForKpi(selectedKPI);
  const currentData = getCurrentPageData(selectedData);
  const totalPageCount = totalPages(selectedData);
  const visiblePages = getVisiblePages(selectedData);

  return (
    <>
      {/* Overlay for outside clicks */}
      <div
        ref={overlayRef}
        className="kpi-overlay"
        onClick={handleBackToGrid}
      ></div>

      <div className="executive-summary selected-kpi-view" ref={contentRef}>
        {/* Header with Navigation */}
        <div className="detail-header">
          <div className="header-left">
            <button className="back-btn" onClick={handleBackToGrid}>
              <i className="fas fa-arrow-left"></i> Back to Overview
            </button>
          </div>
          <div className="header-center">
            <h2 className="detail-title">
              <i className="fas fa-list"></i> {selectedTitle}
              <span className="record-count">
                ({selectedData.length} records)
                {selectedKPI === "avg-duration" && adjustDurationsEnabled && metrics.adjustedOrdersCount > 0 && (
                  <span className="adjustment-badge" title={`${metrics.adjustedOrdersCount} orders capped at 3hrs`}>
                    ⚡ {metrics.adjustedOrdersCount} adjusted
                  </span>
                )}
              </span>
            </h2>
          </div>
          <div className="header-right">
            <div className="kpi-selector">
              <select
                value={selectedKPI || ""}
                onChange={handleKpiChange}
                className="kpi-dropdown"
              >
                <option value="">Switch Metric</option>
                {kpiCards.map((kpi) => (
                  <option key={kpi.id} value={kpi.id}>
                    {kpi.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="data-section">
          <div className="table-container" ref={tableContainerRef}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Cabinet ID</th>
                  <th>Location</th>
                  <th>Borrow Time (Nairobi)</th>
                  <th>Duration</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((order, index) => {
                  const status = getOrderStatus(order);
                  const statusLabel =
                    status === "completed" ? "Completed" : "Pending";
                  const globalIndex =
                    (currentPage - 1) * itemsPerPage + index + 1;
                  const isAdjusted = adjustDurationsEnabled && order.billingDuration && order.billingDuration > 180;
                  const originalDuration = order.billingDuration || 0;
                  const adjustedDuration = getAdjustedDuration(order, adjustDurationsEnabled);

                  return (
                    <tr key={order.id || index} className="table-row">
                      <td>
                        <span className="order-id">
                          #{order.pOrderid || order.pAliorderid || globalIndex}
                        </span>
                      </td>
                      <td>{order.pCabinetid}</td>
                      <td>
                        <div className="location-cell">
                          <i className="fas fa-map-marker-alt"></i>
                          {order.pShopName || "Unassigned"}
                        </div>
                      </td>
                      <td>
                        <div className="time-cell">
                          {chinaToNairobiDisplay(order.pBorrowtime)}
                        </div>
                      </td>
                      <td>
                        <span className="duration-badge">
                          {formatDurationForTable(order.billingDuration || 0, order, adjustDurationsEnabled)}
                          {isAdjusted && (
                            <span 
                              className="adjustment-hint" 
                              title={`Original: ${formatDurationForTable(originalDuration)}`}
                            >
                              ⚡
                            </span>
                          )}
                        </span>
                      </td>
                      <td>
                        <span className="amount-cell">
                          Ksh {(order.pJiesuan || 0).toFixed(2)}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${status}`}>
                          <i
                            className={`fas fa-${
                              status === "completed" ? "check" : "clock"
                            }`}
                          ></i>
                          {statusLabel}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {selectedData.length === 0 && (
              <div className="no-data">
                <i className="fas fa-inbox"></i>
                <h3>No Data Available</h3>
                <p>No records found for the selected metric and date range.</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {selectedData.length > 0 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <i className="fas fa-chevron-left"></i> Previous
              </button>

              <div className="page-numbers">
                {visiblePages.map((page, index) =>
                  page === "..." ? (
                    <span key={`ellipsis-${index}`} className="page-ellipsis">
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      className={`page-btn ${
                        currentPage === page ? "active" : ""
                      }`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  )
                )}
              </div>

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPageCount}
              >
                Next <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ExecutiveSummary;